'use client';

import React from 'react';
import styled, { css } from 'styled-components';
import { Icon } from '@liftango/ui-icons';
import { useDensity } from '@liftango/ui-tokens';

export interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  'aria-label'?: string;
  className?: string;
}

const Wrapper = styled.div<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${(p) => p.theme.colors.neutral.line};
  border-radius: ${(p) => p.theme.radius.md};
  background: ${(p) => p.theme.colors.neutral.panel};
  ${(p) =>
    p.$disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}
  &:focus-within {
    border-color: ${(p) => p.theme.colors.brand.primary};
    outline: ${(p) => p.theme.focus.width} solid ${(p) => p.theme.colors.brand.primary};
    outline-offset: ${(p) => p.theme.focus.insetOffset};
  }
`;

const Input = styled.input`
  border: none;
  background: transparent;
  outline: none;
  width: 48px;
  text-align: center;
  padding: 0;
  font-size: ${(p) => p.theme.fontSize.body};
  font-family: ${(p) => p.theme.fontFamily.mono};
  font-weight: ${(p) => p.theme.fontWeight.semibold};
  color: ${(p) => p.theme.colors.neutral.ink};
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const StepBtn = styled.button<{ $height: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: ${(p) => p.$height}px;
  border: none;
  background: transparent;
  color: ${(p) => p.theme.colors.neutral.inkSoft};
  cursor: pointer;
  &:hover {
    background: ${(p) => p.theme.colors.neutral.panelAlt};
  }
  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

export function NumberInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  disabled,
  'aria-label': ariaLabel,
  className,
}: NumberInputProps) {
  const density = useDensity();
  const canDec = min == null || value - step >= min;
  const canInc = max == null || value + step <= max;

  return (
    <Wrapper $disabled={disabled} className={className}>
      <StepBtn
        $height={density.controlHeight}
        aria-label="Decrement"
        disabled={!canDec}
        onClick={() => canDec && onChange(value - step)}
      >
        <Icon d="M4 8h8" size={12} />
      </StepBtn>
      <Input
        role="spinbutton"
        inputMode="numeric"
        aria-label={ariaLabel}
        aria-valuenow={value}
        aria-valuemin={min}
        aria-valuemax={max}
        value={value}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (!isNaN(n)) onChange(n);
        }}
        disabled={disabled}
      />
      <StepBtn
        $height={density.controlHeight}
        aria-label="Increment"
        disabled={!canInc}
        onClick={() => canInc && onChange(value + step)}
      >
        <Icon name="plus" size={12} />
      </StepBtn>
    </Wrapper>
  );
}
